import { Connection, PublicKey } from '@solana/web3.js';
import { ChainId, NATIVE_SOL } from '../../config/networks';
/**
 * Fetches the token balances for a given owner on the Solana network.
 * @param network - The network on which the tokens exist.
 * @param tokens - The array of token mint addresses.
 * @param owner - The owner's public key.
 * @returns An array of objects each containing the token address, raw balance, balance, and error message (if any).
 */
export async function fetchSVMTokenBalances({ tokens, owner, }) {
    const network = ChainId.SOLANA;
    const connection = new Connection('https://thrumming-evocative-arrow.solana-mainnet.quiknode.pro/b95508bd97189273edee98f183d296d43bd7f4f0/');
    const results = [];
    const ownerPublicKey = new PublicKey(owner);
    for (const token of tokens) {
        try {
            const mintPublicKey = new PublicKey(token);
            if (token === NATIVE_SOL) {
                // Fetch SOL balance
                const balance = await connection.getBalance(ownerPublicKey);
                const decimals = 9; // SOL has 9 decimal places
                results.push({
                    network,
                    token,
                    rawBalance: BigInt(balance),
                    balance: balance / 10 ** decimals,
                    error: '',
                });
            }
            else {
                // Fetch SPL token balance
                const tokenAccounts = await connection.getTokenAccountsByOwner(ownerPublicKey, {
                    mint: mintPublicKey,
                });
                if (tokenAccounts.value.length === 0) {
                    results.push({
                        network,
                        token,
                        rawBalance: BigInt(0),
                        balance: 0,
                        error: 'No token account found',
                    });
                }
                else {
                    const tokenAccount = tokenAccounts.value[0];
                    const accountInfo = await connection.getParsedAccountInfo(tokenAccount.pubkey);
                    if (accountInfo.value) {
                        const parsedInfo = accountInfo.value.data;
                        const rawBalance = BigInt(parsedInfo.parsed.info.tokenAmount.amount);
                        const decimals = parsedInfo.parsed.info.tokenAmount.decimals;
                        results.push({
                            network,
                            token,
                            rawBalance,
                            balance: Number(rawBalance) / 10 ** decimals,
                            error: '',
                        });
                    }
                    else {
                        results.push({
                            network,
                            token,
                            rawBalance: BigInt(0),
                            balance: 0,
                            error: 'Failed to parse account info',
                        });
                    }
                }
            }
        }
        catch (e) {
            results.push({
                network,
                token,
                rawBalance: BigInt(0),
                balance: 0,
                error: `Error fetching token balance: ${e.message}`,
            });
        }
    }
    return results;
}
